import type { RouteLocationNormalized, RouteRecordRaw } from "vue-router";
import { PermissionAttributesEnum } from "@verbleif/lib/src/composables/Permissions/permissions.enum";
import { useTaskViews } from "@verbleif/shared/src/composables/TaskViews/useTaskViews";
import {
  BUILTIN_TASK_VIEW_KEYS,
  isKnownTaskViewKey,
  TASK_VIEW_FALLBACK_KEY,
  TaskListType,
  taskViewRequiresScopeParam,
} from "@web/features/Tasks/TaskViewKeys";
import {
  legacyTaskListRedirect,
  migrateLegacyViewAndIdQuery as migrateQuery,
  normalizeLegacyTaskViewQuery,
} from "@web/features/Tasks/Views/legacyTaskListRouting";
import {
  isPropertyBrowseQuery,
  parseTaskViewScopeFromQuery,
  TASK_VIEW_SCOPE_QUERY,
  TASK_VIEW_SCOPE_QUERY_BY_TYPE,
} from "@web/features/Tasks/Views/taskViewScopeQuery";
import { startViewQuery } from "@web/features/Views/defaultViewQuery";
import { BULK_ASSIGN_ROUTE_NAME, bulkAssignBeforeEnter } from "./BulkAssignee/bulkAssignRoute";
import { isFocusedOnTaskShortcut } from "./Overview/useTaskList";

export {
  BUILTIN_TASK_VIEW_KEYS,
  TASK_VIEW_FALLBACK_KEY as TASK_LIST_FALLBACK_TYPE,
  TaskListType,
  type TaskViewKey,
} from "@web/features/Tasks/TaskViewKeys";

export { LEGACY_TASK_LIST_TYPE_MAP, legacyTaskListRedirect } from "@web/features/Tasks/Views/legacyTaskListRouting";

export function isTaskListType(value: unknown): value is TaskListType {
  return typeof value === "string" && (Object.values(TaskListType) as string[]).includes(value);
}

export function taskListRequiresId(type: TaskListType | null): boolean {
  return type === TaskListType.TYPE_PER_PROPERTY
    || type === TaskListType.TYPE_USER
    || type === TaskListType.TYPE_USER_GROUP
    || type === TaskListType.TYPE_WALK_ROUTE;
}

export const shortcutTaskListTypeMap = {
  [PermissionAttributesEnum.ContextTaskshortcutAlltasks]: TaskListType.TYPE_ALL,
  [PermissionAttributesEnum.ContextTaskshortcutAlltaskstoday]: TaskListType.TYPE_ALL_TODAY,
  [PermissionAttributesEnum.ContextTaskshortcutAlltasksuntiltoday]: TaskListType.TYPE_ALL_UNTIL_TODAY,
  [PermissionAttributesEnum.ContextTaskshortcutChecked]: TaskListType.TYPE_FINISHED,
  [PermissionAttributesEnum.ContextTaskshortcutFinished]: TaskListType.TYPE_READY,
  [PermissionAttributesEnum.ContextTaskshortcutMytaskstoday]: TaskListType.TYPE_TODAY,
  [PermissionAttributesEnum.ContextTaskshortcutMytasksuntiltoday]: TaskListType.TYPE_OPEN_UNTIL_TODAY,
  [PermissionAttributesEnum.ContextTaskshortcutPerproperty]: TaskListType.TYPE_PER_PROPERTY,
  [PermissionAttributesEnum.ContextTaskshortcutWalkroute]: TaskListType.TYPE_ROUTE,
  [PermissionAttributesEnum.ContextTaskshortcutAssignedtome]: TaskListType.TYPE_ASSIGNED_TO_ME,
  [PermissionAttributesEnum.ContextTaskshortcutUnassigned]: TaskListType.TYPE_UNASSIGNED,
};

export const shortcutTaskViewKeyMap: Record<string, string> = Object.fromEntries(
  Object.entries(shortcutTaskListTypeMap).map(([perm, type]) => [perm, normalizeLegacyTaskViewQuery(type)]),
);

function firstEnabledViewWithoutScopeRequirement() {
  const { enabledViews } = useTaskViews();
  return enabledViews.value.find(v => !v.definition.requiredScope)?.key ?? TASK_VIEW_FALLBACK_KEY;
}

/**
 * Where the list opens when the URL names no view. Without view management a user with exactly one
 * shortcut permission keeps starting on that shortcut; everyone else, and everyone with view
 * management, starts on the default list the API resolved (with its choice, a user in a department included).
 */
function taskStartViewQuery(query: Record<string, unknown>): Record<string, string> {
  const { views, viewManagement } = useTaskViews();
  if (!viewManagement.value) {
    const focusedShortcut = isFocusedOnTaskShortcut.value;
    const viewFromShortcut = focusedShortcut ? shortcutTaskViewKeyMap[focusedShortcut] : undefined;
    if (viewFromShortcut) {
      return { view: viewFromShortcut };
    }
  }
  return startViewQuery(views.value, query, TASK_VIEW_SCOPE_QUERY_BY_TYPE, TASK_VIEW_FALLBACK_KEY);
}

/**
 * With view management a department, user or object list is a view with its choice. The links from
 * before (`view=all&userGroup=3`, `view=all&user=41`, `view=all&property=12`, `propertyBrowse=1`)
 * move to that view when it is on; otherwise they keep working as a filter on all tasks.
 */
export function modelATaskListQuery(query: Record<string, string>, enabledKeys: readonly string[]): Record<string, string> | null {
  if (query.view !== BUILTIN_TASK_VIEW_KEYS.ALL) {
    return null;
  }
  const { userGroup, user, property, propertyBrowse } = TASK_VIEW_SCOPE_QUERY;
  const next = { ...query };
  if (query[userGroup] || query[user]) {
    if (!enabledKeys.includes(BUILTIN_TASK_VIEW_KEYS.TASK_LIST)) {
      return null;
    }
    next.view = BUILTIN_TASK_VIEW_KEYS.TASK_LIST;
    return next;
  }
  if (query[property] || isPropertyBrowseQuery(query)) {
    if (!enabledKeys.includes(BUILTIN_TASK_VIEW_KEYS.PER_PROPERTY)) {
      return null;
    }
    delete next[propertyBrowse];
    next.view = BUILTIN_TASK_VIEW_KEYS.PER_PROPERTY;
    return next;
  }
  return null;
}

const VIEWS_READY_TIMEOUT_MS = 5_000;

/** Waits for the effective views so deep links to custom views are not bounced to the fallback while loading. */
function awaitTaskViewsReady(): Promise<void> {
  const { whenReady } = useTaskViews();
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  return Promise.race([
    whenReady().finally(() => {
      if (timeoutId !== undefined) {
        clearTimeout(timeoutId);
      }
    }),
    new Promise<void>((resolve) => {
      timeoutId = setTimeout(resolve, VIEWS_READY_TIMEOUT_MS);
    }),
  ]);
}

export const routes = <RouteRecordRaw[]>[
  {
    path: "/:catchAll(.*)",
    // By path, so the unknown path's catchAll param is not carried over to the task list.
    redirect: { path: "/tasks" },
  },
  { path: "/t/li/:type/:id?/:taskId?", redirect: legacyTaskListRedirect },
  { path: "/t/materials/:dialog?", redirect: to => ({ name: "materials-overview", params: to.params, query: to.query }) },
  { path: "/t/time-entries/:dialog?", redirect: to => ({ name: "time-entries-overview", params: to.params, query: to.query }) },
  { path: "/t/totals", redirect: to => ({ name: "task-totals-overview", query: to.query }) },
  { path: "/t/bulk-assign", redirect: to => ({ name: BULK_ASSIGN_ROUTE_NAME, query: to.query }) },
  { path: "/t", redirect: { name: "task-list" } },
  {
    path: "/tasks",
    name: "tasks",
    component: () => import("@web/features/Tasks/TasksContainer.vue"),
    redirect: to => ({ name: "task-list", query: to.query }),
    children: [
      {
        path: ":taskId(new|\\d+)?",
        name: "task-list",
        component: () => import("./Overview/TasksView.vue"),
        beforeEnter: async (to) => {
          await awaitTaskViewsReady();
          const migrated = migrateQuery(to.query as Record<string, unknown>);
          if (migrated.view === undefined) {
            return { name: "task-list", params: to.params, query: { ...migrated, ...taskStartViewQuery(migrated) } };
          }
          const viewRaw = migrated.view;
          const view = normalizeLegacyTaskViewQuery(viewRaw);
          const { byKey, enabledViews, viewManagement } = useTaskViews();
          const enabledKeys = enabledViews.value.map(v => v.key);

          if (viewManagement.value) {
            const modelA = modelATaskListQuery({ ...migrated, view }, enabledKeys);
            if (modelA) {
              return { name: "task-list", params: to.params, query: modelA };
            }
          }
          const known = isKnownTaskViewKey(view, enabledKeys.length ? enabledKeys : undefined)
            || isKnownTaskViewKey(view);

          if (!known) {
            return {
              name: "task-list",
              params: to.params,
              query: { ...migrated, view: firstEnabledViewWithoutScopeRequirement() },
            };
          }

          const effective = byKey.value.get(view);
          const scope = parseTaskViewScopeFromQuery(migrated);
          // With view management a list without its choice stays: Tasks asks for the choice first.
          if (!viewManagement.value && effective?.definition.requiredScope && !taskViewRequiresScopeParam(effective.definition.requiredScope, scope)) {
            return {
              name: "task-list",
              params: to.params,
              query: { view: firstEnabledViewWithoutScopeRequirement() },
            };
          }

          const focusedShortcut = viewManagement.value ? null : isFocusedOnTaskShortcut.value;
          if (focusedShortcut) {
            const viewFromShortcut = shortcutTaskViewKeyMap[focusedShortcut];
            if (viewFromShortcut && view !== viewFromShortcut) {
              return { name: "task-list", params: to.params, query: { ...migrated, view: viewFromShortcut } };
            }
          }

          const needsMigrate = JSON.stringify(migrated) !== JSON.stringify({ ...to.query, view })
            || !to.query.view;
          if (needsMigrate || view !== viewRaw) {
            return { name: "task-list", params: to.params, query: { ...migrated, view } };
          }

          return true;
        },
      },
      { path: "materials/:dialog?", name: "materials-overview", component: () => import("./MaterialsOverview/MaterialsOverview.vue") },
      { path: "time-entries/:dialog?", name: "time-entries-overview", component: () => import("./TimeEntriesOverview/TimeEntriesOverview.vue") },
      { path: "totals", name: "task-totals-overview", component: () => import("./TaskTotalsOverview/TaskTotalsOverview.vue") },
      {
        path: "bulk-assign",
        name: BULK_ASSIGN_ROUTE_NAME,
        component: () => import("./BulkAssignee/BulkAssignView.vue"),
        beforeEnter: bulkAssignBeforeEnter,
      },
    ],
  },
];

export default routes;

export function isTaskPropertyBrowseRoute(to: RouteLocationNormalized): boolean {
  return isPropertyBrowseQuery(to.query as Record<string, unknown>);
}
